import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { processImage } from '../utils/useBgRemoval';
import './SanjayAssistant.css';

const ROUNDS = [
  { pose: '/wave_hand.png',   msg: "Hi there! Welcome to EBI Education! 👋" },
  { pose: '/pointing_up.png', msg: "Explore our courses — great options await! 📚" },
  { pose: '/presenting.png',  msg: "Sanjay Sir has 15+ years of experience! 🎓" },
  { pose: '/thumbsup.png',    msg: "Quality education at affordable prices! ✅" },
  { pose: '/thinking.png',    msg: "Got a doubt? We're always here to help! 🤔" },
  { pose: '/presenting.png',  msg: "Check our fee structure for your class! 💰" },
  { pose: '/wave_hand.png',   msg: "Contact us anytime — we respond fast! 📞" },
  { pose: '/presenting.png',  msg: "JEE & NEET Foundation covered too! 🚀" },
];

const IDLE_POSES  = ['/sitting.png', '/raising-hand.png'];
const ALL_IMAGES  = [...new Set([...ROUNDS.map(r => r.pose), ...IDLE_POSES])];
const CHAR_W      = 100;
const MARGIN      = 16;

function getDefaultPos() {
  const isMobile = window.innerWidth <= 768;
  const w = isMobile ? 115 : CHAR_W;
  const bottomClear = isMobile ? 90 : 20;
  return {
    x: window.innerWidth - w - MARGIN,
    y: window.innerHeight - (isMobile ? 170 : 150) - bottomClear,
  };
}

export default function SanjayAssistant() {
  const location = useLocation();
  const isAdmin  = location.pathname.startsWith('/admin');

  /* ── position / drag ──────────────────────────── */
  const [pos, setPos]             = useState(null);
  const posRef                    = useRef(null);
  const [isDragging, setDragging] = useState(false);
  const [isSnapping, setSnapping] = useState(false);
  const dragOffset                = useRef({ x: 0, y: 0 });
  const hasDragged                = useRef(false);
  const dragStart                 = useRef({ x: 0, y: 0 });

  /* ── mode ─────────────────────────────────────── */
  const [isActive, setIsActive]   = useState(false);
  const [entered, setEntered]     = useState(false);
  const [excited, setExcited]     = useState(false);

  /* ── idle: alternate sitting / raising-hand ───── */
  const [idleIdx, setIdleIdx]     = useState(0); // 0=sitting, 1=raising-hand
  const idleRef                   = useRef(null);

  /* ── active: message / pose cycling ──────────── */
  const [roundIdx, setRoundIdx]   = useState(0);
  const [displayed, setDisplayed] = useState('');
  const typingRef                 = useRef(null);
  const cycleRef                  = useRef(null);

  /* ── processed images ─────────────────────────── */
  const [imgs, setImgs]           = useState({});

  /* ── welcome fly ─────────────────────────────── */
  const [flyState, setFlyState]   = useState(null);
  const flyRef                    = useRef(null);

  /* derived */
  const charW     = window.innerWidth <= 768 ? 115 : CHAR_W;
  const isOnRight = pos ? pos.x + charW / 2 > window.innerWidth / 2 : true;
  const round     = ROUNDS[roundIdx];
  const activeImg = imgs[round.pose]           || round.pose;
  const idleImg   = imgs[IDLE_POSES[idleIdx]]  || IDLE_POSES[idleIdx];
  const currentImg = isActive ? activeImg : idleImg;

  /* preload all images */
  useEffect(() => {
    Promise.all(ALL_IMAGES.map(src => processImage(src).then(url => [src, url])))
      .then(pairs => setImgs(Object.fromEntries(pairs)));
  }, []);

  /* init position */
  useEffect(() => {
    const p = getDefaultPos();
    setPos(p);
    posRef.current = p;
    const t = setTimeout(() => setEntered(true), 500);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => { posRef.current = pos; }, [pos]);

  /* ── idle loop: sitting → raising-hand → sitting … ── */
  useEffect(() => {
    if (isActive) { clearInterval(idleRef.current); return; }
    setIdleIdx(0);
    idleRef.current = setInterval(() => {
      setIdleIdx(i => (i + 1) % 2);
    }, 1800);
    return () => clearInterval(idleRef.current);
  }, [isActive]);

  /* ── active: typing animation ─────────────────── */
  useEffect(() => {
    if (!isActive) return;
    clearTimeout(typingRef.current);
    setDisplayed('');
    let i = 0;
    const text = round.msg;
    const tick = () => {
      setDisplayed(text.slice(0, i + 1));
      i++;
      if (i < text.length) typingRef.current = setTimeout(tick, 28);
    };
    tick();
    return () => clearTimeout(typingRef.current);
  }, [roundIdx, isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── active: auto-cycle every 5s ─────────────── */
  useEffect(() => {
    if (!isActive) { clearInterval(cycleRef.current); return; }
    cycleRef.current = setInterval(
      () => setRoundIdx(i => (i + 1) % ROUNDS.length),
      5000,
    );
    return () => clearInterval(cycleRef.current);
  }, [isActive]);

  /* snap to edge */
  const snapToEdge = useCallback(() => {
    const cur = posRef.current;
    if (!cur) return;
    const w       = window.innerWidth <= 768 ? 115 : CHAR_W;
    const goLeft  = cur.x + w / 2 < window.innerWidth / 2;
    const targetX = goLeft ? MARGIN : window.innerWidth - w - MARGIN;
    setSnapping(true);
    setPos(p => ({ ...p, x: targetX }));
    setTimeout(() => setSnapping(false), 480);
  }, []);

  /* drag listeners */
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      if (Math.abs(cx - dragStart.current.x) > 5 ||
          Math.abs(cy - dragStart.current.y) > 5) hasDragged.current = true;
      const newX = Math.max(0, Math.min(window.innerWidth  - CHAR_W, cx - dragOffset.current.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 150,    cy - dragOffset.current.y));
      setPos({ x: newX, y: newY });
    };
    const onUp = () => { setDragging(false); snapToEdge(); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [isDragging, snapToEdge]);

  /* welcome fly event */
  useEffect(() => {
    const handler = (e) => {
      if (!posRef.current) return;
      setEntered(false);
      setFlyState({ from: e.detail.rect, to: posRef.current });
    };
    window.addEventListener('sa-welcome-fly', handler);
    return () => window.removeEventListener('sa-welcome-fly', handler);
  }, []);

  /* fly animation */
  useEffect(() => {
    if (!flyState || !flyRef.current) return;
    const el = flyRef.current;
    const { from, to } = flyState;
    const startX = from.left + from.width / 2 - CHAR_W / 2;
    const startY = from.top;
    el.style.transition = 'none';
    el.style.left       = `${startX}px`;
    el.style.top        = `${startY}px`;
    el.style.opacity    = '1';
    requestAnimationFrame(() => requestAnimationFrame(() => {
      el.style.transition = 'left .75s cubic-bezier(.4,0,.2,1), top .75s cubic-bezier(.4,0,.2,1), opacity .2s ease .55s';
      el.style.left       = `${to.x}px`;
      el.style.top        = `${to.y}px`;
    }));
    const t = setTimeout(() => { setFlyState(null); setEntered(true); }, 850);
    return () => clearTimeout(t);
  }, [flyState]);

  /* ── handlers ─────────────────────────────────── */
  const handlePointerDown = (e) => {
    if (e.target.closest('.sa-bubble')) return;
    e.preventDefault();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    dragStart.current  = { x: cx, y: cy };
    dragOffset.current = { x: cx - posRef.current.x, y: cy - posRef.current.y };
    hasDragged.current = false;
    setDragging(true);
  };

  const nextRound = () => {
    clearInterval(cycleRef.current);
    setRoundIdx(i => (i + 1) % ROUNDS.length);
    setExcited(true);
    setTimeout(() => setExcited(false), 400);
    cycleRef.current = setInterval(
      () => setRoundIdx(i => (i + 1) % ROUNDS.length),
      5000,
    );
  };

  const handleCharClick = () => {
    if (hasDragged.current) return;
    if (!isActive) {
      setIsActive(true);
      setRoundIdx(0);
      setExcited(true);
      setTimeout(() => setExcited(false), 400);
    } else {
      nextRound();
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setIsActive(false);
    setIdleIdx(0);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    nextRound();
  };

  if (!pos || isAdmin) return null;

  return (
    <>
      {flyState && (
        <div ref={flyRef} className="sa-fly">
          <img src={currentImg} alt="" className="sa-char-img" />
        </div>
      )}

      <div
        className={[
          'sa-root',
          entered    ? 'sa-root--in'      : '',
          isDragging ? 'sa-root--dragging' : '',
          isSnapping ? 'sa-root--snapping' : '',
        ].join(' ')}
        style={{ left: pos.x, top: pos.y }}
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
      >
        {/* Active: speech bubble */}
        {isActive && (
          <div className={`sa-bubble sa-bubble--${isOnRight ? 'left' : 'right'}`}>
            <p className="sa-bubble__text">
              {displayed}<span className="sa-cursor">|</span>
            </p>
            <button className="sa-bubble__next" onClick={handleNext}>›</button>
            <div className={`sa-bubble__tail sa-bubble__tail--${isOnRight ? 'right' : 'left'}`} />
          </div>
        )}

        {/* Character */}
        <button
          className={`sa-char-btn ${excited ? 'sa-char-btn--excited' : ''}`}
          onClick={handleCharClick}
          aria-label={isActive ? 'Next message' : 'Open assistant'}
        >
          <img
            key={isActive ? round.pose : IDLE_POSES[idleIdx]}
            src={currentImg}
            alt="Sanjay Sir"
            className={[
              'sa-char-img',
              !isActive && idleIdx === 1 ? 'sa-char-img--raise' : '',
            ].join(' ')}
          />
        </button>

        {/* Hint label */}
        {!isActive
          ? <p className="sa-hint sa-hint--tap">Tap me! 👆</p>
          : <p className="sa-hint sa-hint--close" onClick={handleClose}>Tap to close ✕</p>
        }
      </div>
    </>
  );
}
